import Swal from 'sweetalert2';

const SHOULD_RENDER_TOASTS =
  !(typeof import.meta !== 'undefined' && (import.meta as any)?.env?.MODE === 'test');

/**
 * Position options for toast notifications
 * - 'center': Center of the screen (default)
 * - 'top': Top center
 * - 'top-start': Top left
 * - 'top-end': Top right
 * - 'bottom': Bottom center
 * - 'bottom-start': Bottom left
 * - 'bottom-end': Bottom right
 */
export type ToastPosition = 'center' | 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end';

export interface ToastErrorOptions {
  onReport?: () => void;
}

/**
 * Show error toast notification
 * @param title - Error title/message
 * @param text - Optional detailed error text
 * @param position - Optional position (default: 'center')
 * @param options - Optional extras: onReport callback injects a "Report bug" link into the toast
 */
export function toastError(
  title: string,
  text?: string,
  position: ToastPosition = 'center',
  options?: ToastErrorOptions
) {
  if (!SHOULD_RENDER_TOASTS) return;
  void Swal.fire({
    toast: true,
    position,
    icon: 'error',
    title,
    text,
    showConfirmButton: false,
    timer: 4000,
    timerProgressBar: true,
    customClass: {
      popup: position === 'center' ? 'swal2-toast-center' : ''
    },
    ...(options?.onReport
      ? {
          didOpen: (toast: HTMLElement) => {
            const btn = document.createElement('button');
            btn.textContent = 'Report bug';
            btn.setAttribute(
              'style',
              'display:block;margin-top:4px;font-size:11px;color:#6b7280;text-decoration:underline;background:none;border:none;cursor:pointer;padding:0'
            );
            btn.onclick = () => {
              Swal.close();
              options.onReport!();
            };
            toast.appendChild(btn);
          },
        }
      : {}),
  });
}

/**
 * Show info toast notification
 * @param title - Info title/message
 * @param text - Optional detailed info text
 * @param position - Optional position (default: 'center')
 */
export function toastInfo(title: string, text?: string, position: ToastPosition = 'center') {
  if (!SHOULD_RENDER_TOASTS) return;
  void Swal.fire({
    toast: true,
    position,
    icon: 'info',
    title,
    text,
    showConfirmButton: false,
    timer: 2500,
    customClass: {
      popup: position === 'center' ? 'swal2-toast-center' : ''
    }
  });
}

/**
 * Show success toast notification
 * @param title - Success title/message
 * @param text - Optional detailed success text
 * @param position - Optional position (default: 'center')
 */
export function toastSuccess(title: string, text?: string, position: ToastPosition = 'center') {
  if (!SHOULD_RENDER_TOASTS) return;
  void Swal.fire({
    toast: true,
    position,
    icon: 'success',
    title,
    text,
    showConfirmButton: false,
    timer: 4000,
    timerProgressBar: true,
    customClass: {
      popup: position === 'center' ? 'swal2-toast-center' : ''
    }
  });
}

/**
 * Show a SweetAlert2 confirmation dialog (non-blocking, professional UI).
 * Returns true if the user confirmed, false otherwise.
 * @param title - Dialog title
 * @param text - Detailed message
 * @param options - Override defaults (confirmButtonText, cancelButtonText, icon, etc.)
 */
export async function confirmAction(
  title: string,
  text?: string,
  options?: {
    confirmButtonText?: string;
    cancelButtonText?: string;
    icon?: 'warning' | 'question' | 'info' | 'error';
    confirmButtonColor?: string;
  }
): Promise<boolean> {
  if (!SHOULD_RENDER_TOASTS) return true;
  const result = await Swal.fire({
    title,
    text,
    icon: options?.icon ?? 'question',
    showCancelButton: true,
    confirmButtonText: options?.confirmButtonText ?? 'Yes',
    cancelButtonText: options?.cancelButtonText ?? 'Cancel',
    confirmButtonColor: options?.confirmButtonColor ?? '#3085d6',
    reverseButtons: true,
  });
  return !!result.isConfirmed;
}


/**
 * Quiet corner toast for being named by somebody.
 *
 * Deliberately unlike every other toast in this file. A mention is the only
 * notification allowed to interrupt, and the point of it is to be noticed
 * without being in the way — so no icon, no sound, bottom corner rather than
 * centre, and a longer life than the 2.5s of `toastInfo` because somebody who
 * was mentioned is by definition looking at something else.
 *
 * It is also not the mechanism that stops a mention being missed: a toast
 * only works for somebody already watching the tab. What catches the person
 * who stepped away is the unread badge and the tab title, both of which
 * persist. This is the courtesy layer on top of those.
 */
export function toastMention(
  who: string,
  message: string,
  onClick?: () => void
) {
  if (!SHOULD_RENDER_TOASTS) return;
  void Swal.fire({
    toast: true,
    position: 'bottom-end',
    title: `${who} mentioned you`,
    text: message,
    showConfirmButton: false,
    timer: 8000,
    timerProgressBar: true,
    width: '22rem',
    customClass: { popup: 'clu-toast-mention' },
    didOpen: (popup) => {
      if (!onClick) return;
      popup.style.cursor = 'pointer';
      popup.addEventListener('click', () => {
        Swal.close();
        onClick();
      });
    },
  });
}
